package fr.frux.core.maps

import androidx.compose.ui.graphics.Color
import kotlin.jvm.JvmInline
import kotlin.math.abs

/** Identifiant opaque d'un marqueur, cote appelant (ici : l'id de l'arbre). */
@JvmInline
value class MarkerId(
    val value: Long,
)

/**
 * Un marqueur a dessiner sur la carte.
 *
 * Volontairement depourvu de vocabulaire metier : `core-maps` ne sait pas ce qu'est un arbre
 * « cueilli ». C'est l'appelant qui traduit un statut en couleur, ce qui evite une troisieme
 * taxonomie de statut apres `Tree.status` et `FruxStatus` (cf. #87).
 */
data class MapMarker(
    val id: MarkerId,
    val position: Location,
    val tint: Color,
    /**
     * Opacite du marqueur, entre 0 et 1. Volontairement sans vocabulaire metier : c'est
     * l'appelant qui decide ce qu'elle signifie, comme pour [tint].
     */
    val alpha: Float = 1f,
    val label: String? = null,
    val selected: Boolean = false,
    /**
     * Contour discontinu du marqueur (#265, « ? » avant lui). Volontairement sans vocabulaire
     * metier, comme [tint] et [alpha] : l'appelant decide ce que ca signifie. Le sens lisible,
     * lui, se porte par [label] — une denture ne s'annonce pas a un lecteur d'ecran.
     */
    val flagged: Boolean = false,
    /**
     * Cle de glyphe resolue par l'appelant, ou null pour l'icone par defaut. Volontairement
     * sans vocabulaire metier, comme [tint] : `core-maps` ne sait pas ce qu'est une espece.
     */
    val iconKey: String? = null,
    /**
     * Phase « bientôt mûr » (#204) : pilote la couleur du bord de la pastille (ambre plutot que
     * blanc), sur les deux chemins de rendu (SDK et overlay). Volontairement sans vocabulaire
     * metier, comme [tint] : `core-maps` ne sait pas ce que « bientot mur » signifie, seulement
     * quelle couleur en tirer.
     */
    val soon: Boolean = false,
    /**
     * Pastille montree autour du regroupement qui contient ce marqueur (#710), ou null pour n'y
     * rien montrer.
     */
    val badge: MarkerBadge? = null,
)

/**
 * Identifiants situes au meme point cartographique.
 *
 * Une tolerance centimetrique absorbe les arrondis du passage GeoJSON/SDK sans confondre deux
 * arbres réellement distincts. Ce groupe sert quand le SDK renvoie plusieurs marqueurs sous le
 * meme pixel, notamment apres l'eclatement d'un cluster au zoom maximal.
 */
internal fun List<MapMarker>.markerIdsAt(position: Location): List<MarkerId> =
    asSequence()
        .filter { marker -> marker.position.isSameMapPointAs(position) }
        .map { marker -> marker.id }
        .distinct()
        .toList()

private fun Location.isSameMapPointAs(other: Location): Boolean =
    abs(latitude - other.latitude) <= SAME_MAP_POINT_EPSILON &&
        abs(longitude - other.longitude) <= SAME_MAP_POINT_EPSILON

/** Environ un centimetre a l'equateur. */
private const val SAME_MAP_POINT_EPSILON = 0.0000001
